import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  BarChart3,
  CheckCircle2,
  Clock,
  FileText,
  Users,
  Zap,
  ArrowRight
} from 'lucide-react';

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
}

// Animated counter component
function AnimatedCounter({ value, suffix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const startCounting = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;
      
      let start = 0;
      const end = parseInt(value);
      const incrementTime = (duration * 1000) / end;
      
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, incrementTime);
    };
    
    // Use native IntersectionObserver for reliability
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) startCounting(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    
    // Safety fallback: if observer never fires (e.g. parent is opacity:0), start after 3s
    const fallback = setTimeout(() => { if (!hasAnimated.current) startCounting(); }, 3000);
    
    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, [value, duration]);
  
  return <span ref={ref}>{count}{suffix}</span>;
}

// Mobile ambient glow - lightweight CSS-only background effects
function MobileAmbientGlow({ color = 'orange' }) {
  const colors = color === 'orange'
    ? 'from-orange-500/15 to-red-500/10'
    : 'from-teal-500/10 to-emerald-500/10';
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none md:hidden">
      <div className={`absolute w-72 h-72 rounded-full bg-gradient-to-r ${colors} blur-3xl -top-20 -left-20 animate-pulse`} style={{ animationDuration: '4s' }} />
      <div className={`absolute w-56 h-56 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/8 blur-3xl -bottom-16 -right-16 animate-pulse`} style={{ animationDuration: '5s', animationDelay: '2s' }} />
    </div>
  );
}

// Floating gradient orbs for background - Full on desktop, CSS-only on mobile
function FloatingOrbs() {
  return (
    <>
      {/* Desktop: JS-animated orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        <motion.div
          className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-orange-500/20 to-red-500/20 blur-3xl"
          animate={{ x: [0, 100, 0], y: [0, -50, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={{ top: '10%', left: '10%' }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full bg-gradient-to-r from-teal-500/15 to-emerald-500/15 blur-3xl"
          animate={{ x: [0, -80, 0], y: [0, 60, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          style={{ top: '30%', right: '15%' }}
        />
        <motion.div
          className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-purple-500/10 to-pink-500/10 blur-3xl"
          animate={{ x: [0, 50, 0], y: [0, 80, 0], scale: [1.1, 1, 1.1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          style={{ bottom: '20%', left: '20%' }}
        />
      </div>
      {/* Mobile: CSS-only ambient glow */}
      <MobileAmbientGlow color="orange" />
    </>
  );
}

// Scroll-triggered section wrapper - Polished on mobile
function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: isMobile ? 30 : 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: isMobile ? 30 : 60 }}
      transition={{ duration: isMobile ? 0.5 : 0.7, delay: isMobile ? 0.05 : delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Staggered children animation - Visible stagger on mobile
function StaggerContainer({ children, className = '', staggerDelay = 0.1 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        visible: {
          transition: {
            staggerChildren: isMobile ? 0.08 : staggerDelay
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const staggerChild = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  }
};

export default function AdvisorLandingPage() {
  const location = useLocation();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.8]);
  const isMobile = useIsMobile();

  // Scroll to hash on load
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Header */}
      <motion.header 
        className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <motion.div 
                className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="text-white font-bold text-lg">FA</span>
              </motion.div>
              <span className="text-xl font-bold">FinAI</span>
              <span className="text-xs text-gray-400 ml-2">for Advisors</span>
            </Link>

            <nav className="hidden md:flex items-center space-x-4">
              <Link 
                to="/" 
                className="text-gray-400 hover:text-gray-300 text-sm transition-colors"
              >
                Personal Finance App
              </Link>
              <Link to="/advisor/login">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  Login
                </Button>
              </Link>
              <Link to="/advisor/signup">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button size="sm" className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                    Start Free Trial
                  </Button>
                </motion.div>
              </Link>
            </nav>

            <div className="md:hidden flex items-center space-x-2">
              <Link to="/advisor/login">
                <Button variant="ghost" size="sm" className="text-gray-300">
                  Login
                </Button>
              </Link>
              <Link to="/advisor/signup">
                <Button size="sm" className="bg-gradient-to-r from-orange-500 to-red-600">
                  Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative" ref={heroRef}>
        <FloatingOrbs />
        <motion.div style={{ opacity: isMobile ? 1 : heroOpacity }} className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <motion.div 
              className="inline-flex items-center space-x-2 bg-orange-900/30 border border-orange-700 px-4 py-2 rounded-full mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <BarChart3 className="w-4 h-4 text-orange-400" />
              <span className="text-sm font-semibold text-orange-300">FOR FINANCIAL ADVISORS</span>
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}
            >
              Create Professional Financial Plans
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent inline-block">
                10x Faster
              </span>
            </motion.h1>

            <motion.p 
              className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: isMobile ? 0.1 : 0.4 }}
            >
              Generate comprehensive 10+ page financial plans in 5 minutes instead of 2+ hours. 
              Serve more clients, save time, grow your practice.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: isMobile ? 0.2 : 0.6 }}
            >
              <Link to="/advisor/signup">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-lg px-8 py-6 shadow-lg shadow-orange-500/25"
                  >
                    Start 7-Day Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            <motion.p 
              className="text-sm text-gray-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: isMobile ? 0.3 : 0.8 }}
            >
              ✓ No credit card required • Full access to all features • Cancel anytime
            </motion.p>
          </div>

          {/* Stats */}
          <StaggerContainer className="grid grid-cols-3 gap-3 md:gap-6 mt-12 md:mt-16" staggerDelay={0.15}>
            {[
              { icon: Clock, value: "5", suffix: " Min", label: "vs 2+ hours per plan" },
              { icon: FileText, value: "10", suffix: "+", label: "Page PDF reports" },
              { icon: Users, value: "250", suffix: "", label: "Plans/month (Pro)" }
            ].map((stat, i) => (
              <motion.div key={i} variants={staggerChild}>
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card className="bg-gray-800/60 md:bg-gray-800/80 backdrop-blur-sm border-white/5 md:border-gray-700 p-4 md:p-6 text-center hover:border-orange-500/50 transition-all duration-300">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 + i * 0.1, type: "spring", stiffness: 200 }}
                    >
                      <stat.icon className="w-8 h-8 md:w-12 md:h-12 text-orange-400 mx-auto mb-2 md:mb-4" />
                    </motion.div>
                    <h3 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2">
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} duration={1.5} />
                    </h3>
                    <p className="text-xs md:text-sm text-gray-400">{stat.label}</p>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </StaggerContainer>
        </motion.div>
      </section>

      {/* See It In Action Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-800/50 relative">
        <MobileAmbientGlow color="orange" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              See What Your Clients Receive
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              Professional, comprehensive financial plans that impress
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* PDF Preview Mockup - Floating Animation */}
            <AnimatedSection delay={isMobile ? 0 : 0.2}>
              <motion.div 
                className="relative"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="bg-white rounded-lg shadow-2xl p-8">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="border-b-2 border-gray-200 pb-4">
                      <h3 className="text-2xl font-bold text-gray-900">Financial Plan Report</h3>
                      <p className="text-sm text-gray-600">Prepared for: Mr. Rajesh Kumar</p>
                      <p className="text-xs text-gray-500">Date: November 2025</p>
                    </div>

                    {/* Financial Health Score */}
                    <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Financial Health Score</p>
                          <p className="text-3xl font-bold text-green-600">78/100</p>
                          <p className="text-xs text-gray-500">Good</p>
                        </div>
                        <motion.div 
                          className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                        >
                          78
                        </motion.div>
                      </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-2">Cash Flow</p>
                        <div className="flex space-x-1 items-end h-16">
                          <motion.div 
                            className="w-full bg-green-400 rounded-t" 
                            initial={{ height: 0 }}
                            whileInView={{ height: '80%' }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            viewport={{ once: true }}
                          />
                          <motion.div 
                            className="w-full bg-red-400 rounded-t" 
                            initial={{ height: 0 }}
                            whileInView={{ height: '60%' }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            viewport={{ once: true }}
                          />
                          <motion.div 
                            className="w-full bg-blue-400 rounded-t" 
                            initial={{ height: 0 }}
                            whileInView={{ height: '40%' }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                            viewport={{ once: true }}
                          />
                        </div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-xs text-gray-600 mb-2">Asset Allocation</p>
                        <div className="flex items-center justify-center h-16">
                          <motion.div 
                            className="w-16 h-16 rounded-full border-8 border-blue-400 border-t-green-400 border-r-orange-400"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Net Worth:</span>
                        <span className="font-bold text-gray-900">₹45,00,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Monthly Surplus:</span>
                        <span className="font-bold text-green-600">₹25,000</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Retirement Corpus:</span>
                        <span className="font-bold text-blue-600">₹2.5 Cr</span>
                      </div>
                    </div>

                    {/* Page indicator */}
                    <div className="text-center text-xs text-gray-400 mt-4">
                      Page 1 of 10+
                    </div>
                  </div>
                </div>
                {/* Shadow effect */}
                <div className="absolute -bottom-4 -right-4 w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg -z-10"></div>
              </motion.div>
            </AnimatedSection>

            {/* Feature Callouts */}
            <StaggerContainer className="space-y-6" staggerDelay={0.1}>
              {[
                { color: "from-green-500 to-emerald-600", title: "📊 4 Professional Charts", desc: "Visual charts for cash flow, retirement projection, asset allocation, and financial health gauge" },
                { color: "from-blue-500 to-cyan-600", title: "📄 10+ Comprehensive Pages", desc: "Complete coverage: Goals, Retirement, Tax Planning, Insurance, Net Worth, and Action Plan" },
                { color: "from-purple-500 to-pink-600", title: "🎨 Professional Design", desc: "Clean, modern layout with proper formatting, tables, and visual hierarchy" },
                { color: "from-orange-500 to-red-600", title: "⚡ Client-Ready in 5 Minutes", desc: "Download and share immediately. No editing or formatting needed" },
                { color: "from-teal-500 to-emerald-600", title: "🇮🇳 Built for Indian Advisors", desc: "Indian tax laws, INR currency, PPF/EPF/NPS calculations, SEBI guidelines" }
              ].map((feature, i) => (
                <motion.div 
                  key={i} 
                  variants={staggerChild}
                  whileHover={{ x: 10, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="flex items-start space-x-4"
                >
                  <motion.div 
                    className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center flex-shrink-0`}
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-400">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </StaggerContainer>
          </div>

          <AnimatedSection delay={0.3} className="text-center mt-12">
            <Link to="/advisor/signup">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                >
                  See It Yourself - Start Free Trial
                </Button>
              </motion.div>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-20 px-4 bg-gray-800/50 relative">
        <MobileAmbientGlow color="orange" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Scale Your Practice
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              Professional-grade tools designed for Indian financial advisors
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 gap-4 md:gap-8" staggerDelay={0.15}>
            {[
              { icon: Zap, color: "from-orange-500 to-red-600", title: "AI-Powered Plan Generation", desc: "Input client details once and get a complete financial plan covering retirement, tax optimization, insurance, goals, and investment recommendations." },
              { icon: FileText, color: "from-purple-500 to-pink-600", title: "Professional PDF Reports", desc: "Download beautifully formatted 10+ page reports with charts, graphs, and detailed recommendations that impress your clients." },
              { icon: Users, color: "from-teal-500 to-emerald-600", title: "Client Management Dashboard", desc: "Track all your clients, plans, and usage in one centralized dashboard. Never lose track of client progress." },
              { icon: BarChart3, color: "from-blue-500 to-cyan-600", title: "Built for Indian Market", desc: "Tax calculations based on Indian tax laws, INR currency, PPF/EPF/NPS planning, and India-specific investment recommendations." }
            ].map((feature, i) => (
              <motion.div 
                key={i}
                variants={staggerChild}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-start space-x-4 bg-gray-800/40 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none p-4 md:p-0 rounded-xl border border-white/5 md:border-0"
              >
                <motion.div 
                  className={`w-12 h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center flex-shrink-0`}
                  whileHover={{ rotate: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <feature.icon className="w-6 h-6 text-white" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Why Advisors Need This Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-900 relative">
        <div className="container mx-auto max-w-6xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Challenge Financial Advisors Face
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              Your time is valuable. Here's how FinAI helps you focus on what matters.
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Problems */}
            <AnimatedSection delay={0.1}>
              <h3 className="text-2xl font-bold text-red-400 mb-6">❌ Without FinAI</h3>
              <StaggerContainer className="space-y-4" staggerDelay={0.1}>
                {[
                  { emoji: "⏰", title: "2-3 Hours Per Plan", desc: "Manual calculations, Excel sheets, formatting documents" },
                  { emoji: "📝", title: "Limited Client Capacity", desc: "Can only serve 30-50 clients due to time constraints" },
                  { emoji: "😓", title: "Inconsistent Quality", desc: "Plans vary in quality when rushed or tired" },
                  { emoji: "💸", title: "Missing Revenue", desc: "Turn away clients due to capacity limits" }
                ].map((item, i) => (
                  <motion.div key={i} variants={staggerChild}>
                    <Card className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border-white/5 md:border-gray-700 p-4 hover:border-red-500/50 transition-all duration-300">
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">{item.emoji}</div>
                        <div>
                          <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                          <p className="text-sm text-gray-400">{item.desc}</p>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </StaggerContainer>
            </AnimatedSection>

            {/* Solutions */}
            <AnimatedSection delay={0.2}>
              <h3 className="text-2xl font-bold text-green-400 mb-6">✅ With FinAI</h3>
              <StaggerContainer className="space-y-4" staggerDelay={0.1}>
                {[
                  { emoji: "⚡", title: "5 Minutes Per Plan", desc: "AI-powered automation handles calculations & formatting" },
                  { emoji: "📈", title: "Serve 2-3x More Clients", desc: "Scale to 100+ clients without hiring staff" },
                  { emoji: "🎯", title: "Professional Every Time", desc: "Consistent, high-quality 10-page reports with charts" },
                  { emoji: "💰", title: "Grow Revenue", desc: "Accept every client opportunity that comes your way" }
                ].map((item, i) => (
                  <motion.div key={i} variants={staggerChild}>
                    <motion.div whileHover={{ scale: 1.02, x: 5 }}>
                      <Card className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 backdrop-blur-sm border-green-700/50 md:border-green-700 p-4 hover:border-green-500 transition-all duration-300">
                        <div className="flex items-start space-x-3">
                          <div className="text-2xl">{item.emoji}</div>
                          <div>
                            <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                            <p className="text-sm text-gray-300">{item.desc}</p>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  </motion.div>
                ))}
              </StaggerContainer>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-800/50 relative">
        <MobileAmbientGlow color="orange" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Real Scenarios, Real Solutions
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              See how FinAI helps advisors at different stages
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-3 gap-4 md:gap-6" staggerDelay={0.15}>
            {[
              { emoji: "🆕", title: "New Advisor", desc: "Just started your practice? Create professional plans from day one without years of experience.", features: ["Instant credibility", "Compete with established advisors", "Learn best practices from AI"] },
              { emoji: "📊", title: "Scaling Practice", desc: "Growing beyond 30 clients? Serve 100+ clients without hiring additional staff.", features: ["10x your capacity", "Maintain quality at scale", "Focus on client relationships"] },
              { emoji: "💎", title: "Premium Service", desc: "Want to charge more? Deliver 10-page reports with charts that justify premium fees.", features: ["Professional presentation", "Impress high-net-worth clients", "Increase per-client revenue"] }
            ].map((useCase, i) => (
              <motion.div key={i} variants={staggerChild}>
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border-white/5 md:border-gray-700 p-5 md:p-6 h-full hover:border-orange-500/50 transition-all duration-300">
                    <motion.div 
                      className="text-4xl mb-4"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                      {useCase.emoji}
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-3">{useCase.title}</h3>
                    <p className="text-gray-400 mb-4">{useCase.desc}</p>
                    <div className="text-sm text-gray-500">
                      {useCase.features.map((f, j) => (
                        <p key={j} className="mb-2">✓ {f}</p>
                      ))}
                    </div>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-900 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="space-y-3" staggerDelay={0.08}>
            {[
              { q: "Can I cancel anytime?", a: "Yes! No long-term contracts. Cancel anytime during your subscription. Your data remains accessible for 30 days after cancellation." },
              { q: "Do I need technical knowledge?", a: "Not at all. If you can fill a form, you can use FinAI. The interface is designed for financial advisors, not tech experts." },
              { q: "Is my client data secure?", a: "Yes. All data is encrypted with 256-bit SSL encryption. We never share client information with third parties. Your data is backed up daily." },
              { q: "How many clients can I manage?", a: "Each plan has a client limit (Starter: 50, Professional: 100, Growth: 200) and monthly plan creation limits. This ensures you get the capacity you need as your practice grows." },
              { q: "What happens after my trial ends?", a: "After 7 days, you'll need to subscribe to continue creating new plans. Your existing plans remain accessible even if you don't subscribe immediately." },
              { q: "Can I customize the plans for my brand?", a: "Yes! You can add your logo, company name, and choose brand colors for the PDF reports." },
              { q: "Do you offer training or support?", a: "Yes! Professional and Growth plans include priority email support. We respond within 24 hours and help you get the most out of the platform." }
            ].map((faq, i) => (
              <motion.div key={i} variants={staggerChild}>
                <motion.details 
                  className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border border-white/5 md:border-gray-700 rounded-lg p-5 md:p-6 cursor-pointer group hover:border-gray-600 transition-all duration-300"
                  whileHover={{ scale: 1.01 }}
                >
                  <summary className="text-lg font-bold text-white list-none flex items-center justify-between">
                    <span>{faq.q}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <motion.p 
                    className="text-gray-400 mt-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    {faq.a}
                  </motion.p>
                </motion.details>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-20 px-4 relative">
        <MobileAmbientGlow color="orange" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              Choose the plan that fits your practice
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-red-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto" staggerDelay={0.15}>
            {/* Starter */}
            <motion.div variants={staggerChild}>
              <motion.div
                whileHover={{ y: -10, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border-white/5 md:border-gray-700 p-5 md:p-6 h-full hover:border-teal-500/50 transition-all duration-300">
                  <h3 className="text-xl font-bold text-white mb-2">Starter</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">₹749</span>
                    <span className="text-gray-400">/month</span>
                    <div className="mt-1">
                      <span className="text-sm text-gray-400">or </span>
                      <span className="text-sm font-semibold text-white">₹6,499/year</span>
                      <span className="ml-2 text-xs font-bold text-green-400">Save 28%</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {["50 clients", "100 plans/month", "Unlimited PDF downloads", "Custom branding on PDFs", "No FinAI watermark", "Email support"].map((f, i) => (
                      <li key={i} className="flex items-center space-x-2 text-gray-300">
                        <CheckCircle2 className="w-5 h-5 text-teal-500" />
                        <span className={i === 0 ? "font-semibold" : ""}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/advisor/signup">
                    <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-700">
                      Get Started
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            </motion.div>

            {/* Professional */}
            <motion.div variants={staggerChild}>
              <motion.div
                whileHover={{ y: -15, scale: 1.03 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="bg-gradient-to-br from-orange-900/50 to-red-900/50 border-orange-600 p-6 relative pt-10 h-full">
                  <motion.div 
                    className="absolute -top-3 left-1/2 transform -translate-x-1/2"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      MOST POPULAR
                    </span>
                  </motion.div>
                  <h3 className="text-xl font-bold text-white mb-2">Professional</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">₹1,499</span>
                    <span className="text-gray-300">/month</span>
                    <div className="mt-1">
                      <span className="text-sm text-gray-300">or </span>
                      <span className="text-sm font-semibold text-white">₹12,999/year</span>
                      <span className="ml-2 text-xs font-bold text-green-400">Save 28%</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {["100 clients", "250 plans/month", "Unlimited PDF downloads", "Custom branding on PDFs", "No FinAI watermark", "Priority email support"].map((f, i) => (
                      <li key={i} className="flex items-center space-x-2 text-gray-200">
                        <CheckCircle2 className="w-5 h-5 text-orange-400" />
                        <span className={i === 0 ? "font-semibold" : ""}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/advisor/signup">
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                        Start Free Trial
                      </Button>
                    </motion.div>
                  </Link>
                </Card>
              </motion.div>
            </motion.div>

            {/* Growth */}
            <motion.div variants={staggerChild}>
              <motion.div
                whileHover={{ y: -10, scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Card className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border-white/5 md:border-gray-700 p-5 md:p-6 h-full hover:border-teal-500/50 transition-all duration-300">
                  <h3 className="text-xl font-bold text-white mb-2">Growth</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">₹2,999</span>
                    <span className="text-gray-400">/month</span>
                    <div className="mt-1">
                      <span className="text-sm text-gray-400">or </span>
                      <span className="text-sm font-semibold text-white">₹25,999/year</span>
                      <span className="ml-2 text-xs font-bold text-green-400">Save 28%</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {["200 clients", "600 plans/month", "Unlimited PDF downloads", "Custom branding on PDFs", "No FinAI watermark", "Priority support"].map((f, i) => (
                      <li key={i} className="flex items-center space-x-2 text-gray-300">
                        <CheckCircle2 className="w-5 h-5 text-teal-500" />
                        <span className={i === 0 ? "font-semibold" : ""}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/advisor/signup">
                    <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-gray-700">
                      Get Started
                    </Button>
                  </Link>
                </Card>
              </motion.div>
            </motion.div>
          </StaggerContainer>
        </div>
      </section>

      {/* Trust Signals Section */}
      <section className="py-10 md:py-12 px-4 bg-gray-800/50">
        <div className="container mx-auto max-w-6xl">
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 text-center" staggerDelay={0.1}>
            {[
              { emoji: "🔒", title: "Secure & Encrypted", desc: "256-bit SSL encryption" },
              { emoji: "🇮🇳", title: "Built for India", desc: "Indian tax laws & regulations" },
              { emoji: "⚡", title: "Instant Access", desc: "Setup in 2 minutes" },
              { emoji: "💯", title: "7-Day Free Trial", desc: "Full features, no card" }
            ].map((trust, i) => (
              <motion.div key={i} variants={staggerChild}>
                <motion.div
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <motion.div 
                    className="text-3xl mb-2"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                  >
                    {trust.emoji}
                  </motion.div>
                  <h4 className="font-semibold text-white mb-1">{trust.title}</h4>
                  <p className="text-sm text-gray-400">{trust.desc}</p>
                </motion.div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-orange-600 to-red-700 relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(isMobile ? 3 : 5)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute ${isMobile ? 'w-20 h-20' : 'w-32 h-32'} bg-white/5 rounded-full`}
              animate={{
                x: [0, isMobile ? 50 : 100, 0],
                y: [0, isMobile ? -25 : -50, 0],
                scale: [1, 1.2, 1],
                opacity: [0.05, 0.15, 0.05]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                delay: i * 2
              }}
              style={{
                left: `${20 * i}%`,
                top: `${30 + i * 10}%`
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div 
            className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-semibold">🚀 JOIN EARLY ADOPTERS</span>
          </motion.div>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Ready to Transform Your Practice?
          </motion.h2>
          <motion.p 
            className="text-lg text-orange-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Start creating professional plans in 5 minutes instead of 2+ hours
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Link to="/advisor/signup">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  size="lg" 
                  className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl"
                >
                  Start Your 7-Day Free Trial <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
          <motion.p 
            className="text-sm text-orange-100 mt-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            ✓ No credit card required • ✓ Full access to all features • ✓ Cancel anytime
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <motion.div 
                  className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="text-white font-bold text-sm">FA</span>
                </motion.div>
                <span className="text-lg font-bold text-white">FinAI</span>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Professional financial planning platform for Indian advisors
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-semibold text-white mb-3">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a 
                    href="#features" 
                    className="hover:text-white cursor-pointer transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="#pricing" 
                    className="hover:text-white cursor-pointer transition-colors"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Pricing
                  </a>
                </li>
                <li><Link to="/advisor/signup" className="hover:text-white transition-colors">Sign Up</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold text-white mb-3">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors">Personal Finance App</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-white mb-3">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/advisor/login" className="hover:text-white transition-colors">Login</Link></li>
                <li>
                  <a href="mailto:support@finaigpt.com" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
              <p>© {new Date().getFullYear()} FinAI. All rights reserved.</p>
              <p className="mt-4 md:mt-0">
                Looking for personal finance?{' '}
                <Link to="/" className="text-orange-400 hover:text-orange-300 whitespace-nowrap transition-colors">
                  Try our consumer app →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
